// Shared helpers for the Reports & Analytics page.
//
// The reports are pure client-side rollups over data we already have loaded
// in StudentsContext / IEPGoalsContext / IncidentsContext (plus an on-demand
// fetch of goal_progress_entries for the data-collection report). This file
// contains the utilities that show up in more than one report so we're not
// re-implementing "format a date" or "download a CSV" in four places.

/**
 * ISO date (yyyy-mm-dd) for a JS Date, in the local timezone. Comparing
 * date-only columns as strings avoids the timezone drift you get when
 * doing `new Date('2025-09-01') < new Date()` — that first argument gets
 * parsed as UTC midnight and the resulting comparison behaves oddly
 * for anyone east of UTC.
 */
export function toIsoDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Common presets for the date-range picker. Names are the ones a teacher
 * actually thinks in — "this month", "this quarter", "school year". The
 * quarter definition uses the standard calendar-quarter definition.
 */
export function computeDateRangePreset(preset, now = new Date()) {
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  switch (preset) {
    case 'this-week': {
      const day = startOfToday.getDay();
      const monday = new Date(startOfToday);
      monday.setDate(startOfToday.getDate() - ((day + 6) % 7));
      return { from: toIsoDate(monday), to: toIsoDate(startOfToday) };
    }
    case 'this-month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: toIsoDate(first), to: toIsoDate(startOfToday) };
    }
    case 'last-30-days': {
      const start = new Date(startOfToday);
      start.setDate(startOfToday.getDate() - 30);
      return { from: toIsoDate(start), to: toIsoDate(startOfToday) };
    }
    case 'this-quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const first = new Date(now.getFullYear(), q * 3, 1);
      return { from: toIsoDate(first), to: toIsoDate(startOfToday) };
    }
    case 'school-year': {
      // A school year runs August of year N through July of year N+1.
      // In August-Dec we're in the 'this-year -> next-year' band; in
      // Jan-July we're finishing the 'last-year -> this-year' band.
      const year =
        now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
      const first = new Date(year, 7, 1);
      return { from: toIsoDate(first), to: toIsoDate(startOfToday) };
    }
    case 'year-to-date': {
      const first = new Date(now.getFullYear(), 0, 1);
      return { from: toIsoDate(first), to: toIsoDate(startOfToday) };
    }
    default:
      return null;
  }
}

/**
 * Human-readable label for a range. Used for the report title.
 */
export function formatDateRangeLabel({ from, to }) {
  if (!from && !to) return 'All time';
  const fromLabel = from ? new Date(from).toLocaleDateString() : '…';
  const toLabel = to ? new Date(to).toLocaleDateString() : 'today';
  return `${fromLabel} – ${toLabel}`;
}

/**
 * Predicate: does an ISO date/timestamp fall inside the [from, to] range?
 * `from` and `to` are date strings (yyyy-mm-dd) inclusive on both ends.
 * Null bounds mean "no lower bound" / "no upper bound".
 */
export function inRange(value, { from, to } = {}) {
  if (!value) return false;
  const iso =
    value instanceof Date
      ? toIsoDate(value)
      : String(value).slice(0, 10);
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
}

/**
 * Given an array of {date, ...} rows, bucket by month ("YYYY-MM") and
 * sum the given field per bucket. Zero-fills missing months in the
 * range so the resulting chart has an unbroken x-axis.
 */
export function bucketByMonth(rows, dateKey, valueKey, range) {
  const counts = new Map();
  for (const row of rows) {
    const raw = row?.[dateKey];
    if (!raw) continue;
    const key = String(raw).slice(0, 7); // YYYY-MM
    const inc = valueKey ? Number(row[valueKey] ?? 0) : 1;
    counts.set(key, (counts.get(key) || 0) + inc);
  }

  const months = [];
  if (range?.from && range?.to) {
    const from = new Date(range.from);
    const to = new Date(range.to);
    const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
    while (cursor <= to) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      months.push(`${y}-${m}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }
  } else {
    for (const key of counts.keys()) months.push(key);
    months.sort();
  }

  return months.map((key) => ({
    month: key,
    label: new Date(`${key}-01T12:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      year: '2-digit',
    }),
    value: counts.get(key) || 0,
  }));
}

/**
 * Given an array of {date, ...} rows, bucket by day (YYYY-MM-DD).
 * Zero-fills days inside the given range so the chart is continuous.
 */
export function bucketByDay(rows, dateKey, range) {
  const counts = new Map();
  for (const row of rows) {
    const raw = row?.[dateKey];
    if (!raw) continue;
    const key = String(raw).slice(0, 10);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const days = [];
  if (range?.from && range?.to) {
    const from = new Date(`${range.from}T00:00:00`);
    const to = new Date(`${range.to}T00:00:00`);
    const cursor = new Date(from);
    while (cursor <= to) {
      const key = toIsoDate(cursor);
      days.push(key);
      cursor.setDate(cursor.getDate() + 1);
    }
  } else {
    for (const key of counts.keys()) days.push(key);
    days.sort();
  }

  return days.map((key) => ({
    day: key,
    label: new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    }),
    value: counts.get(key) || 0,
  }));
}

/**
 * CSV export: given a rows array (objects) and an ordered list of column
 * headers, produce a CSV blob and trigger a browser download. Handles
 * embedded quotes, commas, and newlines using RFC 4180 quoting rules.
 */
export function downloadCsv(filename, rows, columns) {
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines = [];
  lines.push(columns.map((c) => escape(c.label ?? c.key)).join(','));
  for (const row of rows) {
    lines.push(columns.map((c) => escape(row?.[c.key])).join(','));
  }

  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Given a student list, build a fast id -> "First Last" name lookup.
 * Reused in every report component.
 */
export function studentNameLookup(students) {
  const map = new Map();
  for (const s of Array.isArray(students) ? students : []) {
    const name =
      `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student';
    map.set(s.id, name);
  }
  return map;
}

/**
 * Palette that stays consistent with the rest of the app.
 */
export const CHART_COLORS = {
  primary: '#2563EB',
  success: '#059669',
  warning: '#F59E0B',
  danger: '#DC2626',
  purple: '#8B5CF6',
  slate: '#64748B',
  cyan: '#06B6D4',
  pink: '#EC4899',
};

export const CATEGORICAL_PALETTE = [
  CHART_COLORS.primary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.danger,
  CHART_COLORS.purple,
  CHART_COLORS.cyan,
  CHART_COLORS.pink,
  CHART_COLORS.slate,
];

/**
 * buildPrintReportUrl — construct a URL to the dedicated print route
 * for a specific report, preserving the currently-selected date range
 * and student filter as query params. Used by the "Print / Save as PDF"
 * button on every report tab.
 *
 * `autoprint=1` tells the print page to auto-invoke window.print() as
 * soon as the charts finish laying out. A user who navigates directly
 * to the URL (bookmark, browser history) doesn't get the auto-print
 * — they see the preview and click "Print" themselves.
 */
export function buildPrintReportUrl(reportType, { dateRange, studentFilter } = {}) {
  const params = new URLSearchParams();
  if (dateRange?.from) params.set('from', dateRange.from);
  if (dateRange?.to) params.set('to', dateRange.to);
  if (studentFilter && studentFilter.size > 0) {
    params.set('students', Array.from(studentFilter).join(','));
  }
  params.set('autoprint', '1');
  const qs = params.toString();
  return `/print/reports/${encodeURIComponent(reportType)}${qs ? `?${qs}` : ''}`;
}
